import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Badge, Menu, Box, Typography, Chip, Divider, Button } from '@mui/material';
import { Notifications, Circle, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { alertasService } from '../services/api';

const severidadeColors: Record<string, string> = {
  critico: '#DC2626',
  alto: '#F59E0B',
  informativo: '#3B82F6',
};

const AlertaBadge: React.FC = () => {
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const [count, setCount] = useState(0);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const fetchCount = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      const data = await alertasService.getNaoLidos(municipioAtivo.id);
      setCount(data.count);
    } catch {
      // silently fail
    }
  }, [municipioAtivo?.id]);

  const fetchRecent = useCallback(async () => {
    if (!municipioAtivo?.id) return;
    try {
      const data = await alertasService.getHistorico(municipioAtivo.id, 1, 5, undefined, 'novo');
      setRecentAlerts(data.data || []);
    } catch {
      // silently fail
    }
  }, [municipioAtivo?.id]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // poll a cada 60s
    return () => clearInterval(interval);
  }, [fetchCount]);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchRecent();
  };

  const handleMarkRead = async (id: number) => {
    try {
      await alertasService.updateStatus(id, 'lido', municipioAtivo!.id);
      setRecentAlerts((prev) => prev.filter((a) => a.id !== id));
      setCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{ color: '#7A7B8E', '&:hover': { backgroundColor: '#F0F0F5' } }}
      >
        <Badge
          badgeContent={count}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              minWidth: 18,
              height: 18,
              ...(count === 0 && { display: 'none' }),
            },
          }}
        >
          <Notifications sx={{ fontSize: 22 }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            mt: 1,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgb(0 0 0 / 0.15)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#24252E' }}>
            Alertas
          </Typography>
          {count > 0 && (
            <Chip
              size="small"
              label={`${count} novo${count > 1 ? 's' : ''}`}
              sx={{ backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: '0.7rem', fontWeight: 600, height: 22 }}
            />
          )}
        </Box>

        {/* Alert List */}
        <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
          {recentAlerts.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: '#7A7B8E' }}>
                Nenhum alerta novo
              </Typography>
            </Box>
          ) : (
            recentAlerts.map((alert) => (
              <Box
                key={alert.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#F8F8FA' },
                  transition: 'background-color 0.15s',
                }}
                onClick={() => handleMarkRead(alert.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Circle sx={{ fontSize: 10, mt: 0.5, color: severidadeColors[alert.severidade] || '#6B7280', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#24252E', lineHeight: 1.3 }} noWrap>
                      {alert.titulo}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#7A7B8E', lineHeight: 1.4, mt: 0.25 }} noWrap>
                      {alert.resumo}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6875rem', color: '#AAAAB9', mt: 0.5 }}>
                      {new Date(alert.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
            onClick={() => { setAnchorEl(null); navigate('/alertas'); }}
            sx={{
              textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#007AF0',
              '&:hover': { backgroundColor: 'rgba(0,122,240,0.08)' },
            }}
          >
            Ver todos os alertas
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default AlertaBadge;
